import { apiCustomerPublic } from "@/api"
import { feedbackFailureOf, type FeedbackSubmission, type OrderFeedback } from "@/domain/order-feedback/order-feedback"
import { isAxiosError } from "axios"

/** One customer-meaningful status change of a tracked order. */
export type TrackedOrderStep = { status: string; at: string }

/** One item of a tracked order. */
export type TrackedOrderLine = { name: string; quantity: number; totalCents: number; options: string[] }

/** The masked, login-free order the `/p/:token` page shows (customer-service-gateway `GET /public/track/:token`). */
export type TrackedOrder = {
  displayId: string
  type: "DELIVERY" | "TAKEOUT" | "INDOOR" | string
  status: string
  timeline: TrackedOrderStep[]
  lines: TrackedOrderLine[]
  itemsCents: number
  feesCents: number
  discountCents: number
  totalCents: number
  etaStart?: string
  etaEnd?: string
  maskedAddress?: string
  storeName: string
  merchantId: string
  placedAt: string
  expiresAt?: string
  rateable: boolean
  feedback?: OrderFeedback
}

/** Result of one tracking read: the order, or why there is none. */
export type TrackedOrderResult = { kind: "ok"; order: TrackedOrder } | { kind: "notFound" } | { kind: "error" }

/** Polling cadence of the tracking page while the order is live. */
export const TRACKING_POLL_MS = 15_000

const TERMINAL = new Set(["DELIVERED", "CONCLUDED", "CANCELLED"])

/** True once nothing will change anymore (delivered, concluded, cancelled, or a pickup already collected). */
export function isTrackedOrderFinished(order: TrackedOrder): boolean {
  if (TERMINAL.has(order.status)) return true
  return order.status === "PICKED_UP" && order.type !== "DELIVERY"
}

/** Reads the tracked order; a 404 means the link is unknown or expired. */
export async function fetchTrackedOrder(token: string): Promise<TrackedOrderResult> {
  try {
    const { data } = await apiCustomerPublic.get<TrackedOrder>(`/public/track/${encodeURIComponent(token)}`)
    return { kind: "ok", order: data }
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) return { kind: "notFound" }
    return { kind: "error" }
  }
}

/** Rates the tracked order without login; the token proves ownership. */
export async function submitTrackedOrderFeedback(token: string, stars: number, comment: string): Promise<FeedbackSubmission> {
  try {
    const { data } = await apiCustomerPublic.post<{ feedback: OrderFeedback; created: boolean; alreadyRated?: boolean }>(
      `/public/track/${encodeURIComponent(token)}/feedback`,
      { stars, comment },
    )
    return { feedback: data.feedback, alreadyRated: data.alreadyRated ?? !data.created }
  } catch (error) {
    throw feedbackFailureOf(error)
  }
}

/** The four milestones of the progress bar for the order type. */
export type Milestone = { key: string; label: string }

/** Milestones in order: received → preparing → on the way / ready → delivered / collected. */
export function milestonesFor(type: string): Milestone[] {
  if (type === "DELIVERY") {
    return [
      { key: "received", label: "Recebido" },
      { key: "preparing", label: "Em preparo" },
      { key: "onTheWay", label: "A caminho" },
      { key: "done", label: "Entregue" },
    ]
  }
  return [
    { key: "received", label: "Recebido" },
    { key: "preparing", label: "Em preparo" },
    { key: "ready", label: "Pronto" },
    { key: "done", label: type === "INDOOR" ? "Entregue" : "Retirado" },
  ]
}

/** Index (0..3) of the milestone the status reached, -1 when cancelled. */
export function milestoneIndex(status: string, type: string): number {
  switch (status) {
    case "CANCELLED":
      return -1
    case "PREPARATION_REQUESTED":
    case "PREPARING":
      return 1
    case "READY_FOR_PICKUP":
      return type === "DELIVERY" ? 1 : 2
    case "DISPATCHED":
      return 2
    case "PICKED_UP":
      return type === "DELIVERY" ? 2 : 3
    case "DELIVERED":
    case "CONCLUDED":
      return 3
    default:
      return 0
  }
}
